import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { toast } from "sonner";
import { Camera, Sparkles, Loader2, X } from "lucide-react";
import { photoSrc } from "@/components/ObservationDialog";

// Photos d'un compteur ou d'une clé + lecture IA (Claude) pour pré-remplir les champs
export default function ItemPhotos({ etatId, genre, photos = [], onPhotos, onLecture, disabled, testid, uploadUrl, lectureUrl }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(null);
  const url = uploadUrl || `/etats/${etatId}/photos`;
  const readUrl = lectureUrl || `/etats/${etatId}/lecture-photo`;

  const upload = async (files) => {
    setBusy(true);
    try {
      const added = [];
      for (const f of Array.from(files).slice(0, 4)) {
        const fd = new FormData(); fd.append("file", await compressImage(f));
        const { data } = await api.post(url, fd);
        added.push({ id: data.id, storage_path: data.storage_path, url: data.url });
      }
      onPhotos([...photos, ...added]);
      if (added.length && onLecture) lire(genre === "cle" ? [...photos, ...added].map((p) => p.id) : [added[0].id]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };

  const lire = async (ids) => {
    const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
    if (!list.length) return;
    setReading(list[0]);
    try {
      const { data } = await api.post(readUrl, { file_id: list[0], file_ids: list, genre });
      onLecture(data);
      toast.success(`Lecture IA (${data.confiance || "—"}) : vérifiez les valeurs proposées`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setReading(null); }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid}>
      {photos.map((ph) => (
        <div key={ph.id} className="relative group">
          <img src={photoSrc(ph)} alt="" className="h-11 w-11 object-cover rounded border border-dd-light" />
          {!disabled && onLecture && (
            <button onClick={() => lire(genre === "cle" ? photos.map((p) => p.id) : [ph.id])} title="Lire avec l'IA" data-testid={`${testid}-lire-${ph.id}`}
              className="absolute -bottom-1 -left-1 h-5 w-5 rounded-full bg-dd-blue text-white flex items-center justify-center">
              {reading === ph.id ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
            </button>
          )}
          {!disabled && (
            <button onClick={() => onPhotos(photos.filter((p) => p.id !== ph.id))} title="Retirer" data-testid={`${testid}-remove-${ph.id}`}
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white border border-dd-light text-destructive flex items-center justify-center">
              <X size={11} />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <>
          <input ref={input} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => e.target.files?.length && upload(e.target.files)} />
          <button onClick={() => input.current?.click()} disabled={busy} title="Photographier" data-testid={`${testid}-add`}
            className="h-11 w-11 rounded-md border border-dashed border-dd-blue/50 text-dd-blue flex items-center justify-center hover:bg-dd-light/60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
          </button>
        </>
      )}
    </div>
  );
}
